import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import Navbar from "@/components/Navbar";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
function googleLogin() {
  const redirectUrl = window.location.origin + "/dashboard";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
}

export default function Login() {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [form, setForm] = useState({ email: "", password: "" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", form);
      setUser(data.user);
      toast.success("Welcome back.");
      navigate("/dashboard");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Login failed.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-cream min-h-screen" data-testid="login-page">
      <Navbar />
      <div className="max-w-md mx-auto px-6 pt-40 pb-24">
        <p className="overline text-clay mb-4">Welcome back</p>
        <h1 className="font-serif font-light text-4xl md:text-5xl text-forest mb-10">Sign in</h1>

        <form onSubmit={submit} className="space-y-4">
          <Input type="email" placeholder="Email address" required value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            className="rounded-none bg-white border-border focus-visible:ring-forest h-12" data-testid="login-email" />
          <Input type="password" placeholder="Password" required value={form.password}
            onChange={(e) => setForm({ ...form, password: e.target.value })}
            className="rounded-none bg-white border-border focus-visible:ring-forest h-12" data-testid="login-password" />
          <Button type="submit" disabled={loading}
            className="w-full rounded-none bg-forest hover:bg-forest-dark text-cream h-12 transition-colors duration-300" data-testid="login-submit-btn">
            {loading ? "Signing in…" : "Sign in"}
          </Button>
        </form>

        <div className="flex items-center gap-4 my-6 text-muted-foreground text-sm">
          <span className="flex-1 h-px bg-border" /> or <span className="flex-1 h-px bg-border" />
        </div>

        <Button onClick={googleLogin} variant="outline"
          className="w-full rounded-none border-forest text-forest hover:bg-surfacealt h-12 gap-2" data-testid="login-google-btn">
          <svg className="w-5 h-5" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z"/><path fill="#FBBC05" d="M5.84 14.1a6.6 6.6 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88l3.66-2.84z"/><path fill="#EA4335" d="M12 4.75c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 1.46 14.97.5 12 .5A11 11 0 0 0 2.18 7.06l3.66 2.84C6.71 6.68 9.14 4.75 12 4.75z"/></svg>
          Continue with Google
        </Button>

        <p className="text-center text-muted-foreground text-sm mt-8">
          New here? <Link to="/register" className="text-forest underline underline-offset-4 hover:text-clay" data-testid="to-register-link">Create an account</Link>
        </p>
      </div>
    </div>
  );
}
